import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { SiteFooter } from "@/components/SiteFooter";

const CONTENT = {
  privacy: {
    title: "Privacy — invited trial",
    intro:
      "This describes how information is handled during the KATLAS Cierge Design Studio invited discovery test. It carries the same material facts as the notice shown on the intake page.",
    sections: [
      {
        h: "What we collect",
        p: "For each invited discovery you provide three details: a public LinkedIn profile URL, a public website URL, and an email address for the report. We do not collect accounts, passwords or payment information.",
      },
      {
        h: "How it is used",
        p: "The KATLAS team reviews the three details and may read the public pages they identify. Relevant public-source information may be processed using OpenAI to assist with exploratory mapping. Your report email address is used only to deliver the report and manage the invited test; it is not included in any AI mapping prompt.",
      },
      {
        h: "What you should not submit",
        p: "Please submit only public links and an email address you are authorised to provide. Do not submit customer records, facial scans, passwords, payment information, confidential contracts, trade secrets, legally privileged material or other sensitive information.",
      },
      {
        h: "Retention",
        p: "Submitted details, working analysis and the report may be retained for up to 30 days after the report and feedback discussion, then deleted from the active trial workspace, unless KATLAS and the participant agree to continue into a prototype stage.",
      },
    ],
  },
  terms: {
    title: "Terms — invited trial",
    intro:
      "These concise terms apply to the KATLAS Cierge Design Studio invited discovery test only.",
    sections: [
      {
        h: "Status of this test",
        p: "This is an invited early testing process. It is not a production KATLAS implementation, an automated SaaS service, a live KATLAS wallet or node journey, or a formal audit or assessment.",
      },
      {
        h: "No fee",
        p: "The invited trial fee is £0. No payment details are collected. The future paid service and commercial terms are not active in this walkthrough.",
      },
      {
        h: "Manual preparation",
        p: "The first report is prepared manually by the KATLAS team and delivered by email. OpenAI may assist with structuring the exploratory mapping.",
      },
      {
        h: "Nature of the report",
        p: "The report is exploratory, may contain errors and is not professional legal, regulatory, financial, medical, security or compliance advice.",
      },
    ],
  },
  contact: {
    title: "Contact — invited trial",
    intro:
      "For questions about your invited KATLAS Cierge Design Studio discovery, please use the contact below.",
    sections: [
      {
        h: "KATLAS",
        p: "Please contact the KATLAS team via katlastechnology.com, and reference the invited-test record shown on your confirmation page where possible.",
      },
      {
        h: "About KATLAS",
        p: "KATLAS provides runtime operational-governance infrastructure. Cierge Design Studio is its applied discovery and pilot studio. Learn more at katlastechnology.com.",
      },
    ],
  },
};

export default function TrialInfo({ page }) {
  const data = CONTENT[page];
  return (
    <div className="min-h-screen bg-stone-base paper-grain">
      <header className="mx-auto max-w-2xl px-6 pt-10">
        <BrandMark />
      </header>
      <main className="mx-auto max-w-2xl px-6 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted transition-colors hover:text-teal"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Link>
        <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-deep">
          Invited trial information — draft for testing
        </p>
        <h1 className="mt-2 font-serif text-[32px] leading-tight text-ink">
          {data.title}
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed text-ink-soft">{data.intro}</p>
        <div className="mt-8 space-y-7">
          {data.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-serif text-[19px] text-ink">{s.h}</h2>
              <p className="mt-2 text-[14px] leading-relaxed text-muted">{s.p}</p>
            </section>
          ))}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
